/* eslint-disable @typescript-eslint/no-explicit-any */
export type Enum = { [s: number]: string };

// Result:
// isEnumKey(DayOfWeek, DayOfWeek[DayOfWeek.Friday]) // true
function isEnumKey<T extends Enum>(enumSrc: T, key: unknown): key is keyof T {
  return Number.isInteger(enumSrc[key as keyof T]);
}

// Result:
// isEnumValue(DayOfWeek, DayOfWeek.Friday) // true
function isEnumValue<T extends Enum>(enumSrc: T, value: unknown): value is T[keyof T] {
  return Number.isInteger(enumSrc[enumSrc[value as keyof T] as any as keyof T]);
}

/*
Result:
[
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]
*/
function enumToKeys<T extends Enum>(enumSrc: T): (keyof T)[] {
  return Object.keys(enumSrc).filter((key: keyof T | any) => isEnumKey(enumSrc, key)) as (keyof T)[];
}

// Result:
// [ 1, 2, 3, 4, 5, 6, 7 ]
function enumToValues<T extends Enum>(enumSrc: T): T[keyof T][] {
  return enumToKeys(enumSrc).map((key: keyof T) => enumSrc[key]);
}

// enumValueToKey(DayOfWeek, DayOfWeek.Friday); // Friday
function enumValueToKey<T extends Enum>(enumSrc: T, value: T[keyof T]): keyof T | undefined {
  return (enumSrc as any)[value];
}

/*
Result:
[
  [ 'Monday', 1 ],
  [ 'Tuesday', 2 ],
  [ 'Wednesday', 3 ],
  [ 'Thursday', 4 ],
  [ 'Friday', 5 ],
  [ 'Saturday', 6 ],
  [ 'Sunday', 7 ]
]
*/
function enumToEntries<T extends Enum>(enumSrc: T): [keyof T, T[keyof T]][] {
  return enumToValues(enumSrc).map((value: T[keyof T]) => [enumValueToKey(enumSrc, value) as keyof T, value]);
}

/*
Result:
[
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 }
]
*/
function fromEnum<T extends Enum, C>(
  enumSrc: T,
  projection: (item: [keyof T, T[keyof T]], index: number, array: [keyof T, T[keyof T]][]) => C,
  skip?: (value: [keyof T, T[keyof T]], index: number, array: [keyof T, T[keyof T]][]) => boolean
) {
  let entries = enumToEntries(enumSrc);

  if (skip) entries = entries.filter(skip);

  return entries.map(projection);
}

export { isEnumKey, isEnumValue, enumToKeys, enumToValues, enumValueToKey, enumToEntries, fromEnum };
